/* eslint-disable react/no-unstable-nested-components */
import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import useDocumentTitle from "hooks/useDocumentTitle";
import { Badge } from "primereact/badge";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { Chip } from "primereact/chip";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Message } from "primereact/message";
import { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useGetAllBaseRateFilesQuery } from "store/queries/baseRateFiles";
import {
  useGetContractFeeScheduleQuery,
  RateCalculation,
  useExportFeeScheduleMutation,
  useGenerateProviderProceduresMutation,
  useUpdateContractFeeScheduleMutation,
  useActivateContractFeeScheduleMutation,
} from "store/queries/contractFeeSchedules";
import { authSelector } from "store/slices/auth";
import { SpecialtyList } from "types/Provider/Provider";

export function ContractViewPage() {
  const { id, contractId } = useParams<{ id: string; contractId?: string }>();
  const navigate = useNavigate();

  // In hospital context, use contractId; in admin context, use id
  const actualContractId = contractId || id;
  const hospitalId = contractId ? id : null; // If contractId exists, then id is the hospital ID
  useDocumentTitle("Contract Details");
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);
  const { token } = useSelector(authSelector);

  const {
    data: contractData,
    isLoading,
    error,
    refetch,
  } = useGetContractFeeScheduleQuery(actualContractId!);
  const { data: baseRateFiles } = useGetAllBaseRateFilesQuery({});
  const [exportFeeSchedule] = useExportFeeScheduleMutation();
  const [generateProcedures] = useGenerateProviderProceduresMutation();
  const [updateContract] = useUpdateContractFeeScheduleMutation();
  const [activateContract] = useActivateContractFeeScheduleMutation();

  const contract = contractData?.data;
  const isAnesthesiologist =
    contract?.provider?.specialty === SpecialtyList.ANESTHESIOLOGIST;

  // Generate procedures dialog state
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [acknowledgeOverwrite, setAcknowledgeOverwrite] = useState(false);
  const [dryRunResult, setDryRunResult] = useState<any>(null);

  useEffect(() => {
    setIsLoading(isLoading);
  }, [isLoading, setIsLoading]);

  useEffect(() => {
    if (error) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load contract details.",
      });
    }
  }, [error, toastElement]);

  // Auto-refresh when contract is importing
  useEffect(() => {
    if (contract?.status === "Importing") {
      const interval = setInterval(() => {
        refetch();
      }, 5000);

      return () => clearInterval(interval);
    }
    return () => {};
  }, [contract?.status, refetch]);

  if (!contract) {
    return (
      <div className="card">
        <div className="text-center p-4">
          {isLoading ? "Loading contract details..." : "Contract not found"}
        </div>
      </div>
    );
  }

  const statusSeverityMap = {
    Draft: "info",
    Active: "success",
    Inactive: "warning",
    Expired: "danger",
    Importing: "warning",
  } as const;

  const getBaseRateFileName = (baseRateFileId?: string) => {
    if (!baseRateFileId) return "No base rate file";
    const file = baseRateFiles?.data?.find((f) => f.id === baseRateFileId);
    return file ? file.displayName : "Base Rate File";
  };

  const getButtonLabel = (status?: string, dryRunResult?: any) => {
    if (status === "Draft") {
      return dryRunResult ? "Activate Contract" : "Activate";
    }
    return dryRunResult ? "Generate" : "Generate";
  };

  const rateCalculationBodyTemplate = (rowData: RateCalculation) => {
    const baseRateFile = baseRateFiles?.data?.find(
      (f) => f.id === rowData.baseRateFileId
    );
    const procedureCount = baseRateFile?.metadata?.rowCount;
    const baseRateFileName = getBaseRateFileName(rowData.baseRateFileId);
    const procedureText =
      procedureCount !== undefined
        ? ` (${procedureCount.toLocaleString()} procedures)`
        : "";

    return (
      <div className="text-900">
        {isAnesthesiologist
          ? `$${(rowData.ratePercent / 100).toFixed(
              2
            )} per ${baseRateFileName}${procedureText}`
          : `${rowData.ratePercent}% of ${baseRateFileName}${procedureText}`}
      </div>
    );
  };

  return (
    <div className="card">
      {/* Outdated Procedures Warning */}
      {contract.proceduresOutdated && contract.status === "Active" && (
        <Message
          severity="warn"
          className="mb-4"
          text="The base rate files for this contract have been updated since the provider procedures were last generated. To ensure accurate pricing, please regenerate the procedures using the 'Re-generate Procedures' button."
        />
      )}

      <div className="flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="m-0 mb-2">
            {contract.provider?.name} - {contract.payer?.name}
          </h2>
          <div className="flex align-items-center gap-2">
            <Badge
              value={contract.status}
              severity={statusSeverityMap[contract.status]}
            />
            {contract.proceduresOutdated && contract.status === "Active" && (
              <span title="Base rates have been updated. Procedures need regeneration.">
                <Badge value="Outdated" severity="warning" className="ml-2" />
              </span>
            )}
            {contract.status === "Importing" && (
              <span className="text-warning ml-2">
                <i className="pi pi-spin pi-spinner mr-2" />
                Fee schedule is being imported...
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            label="Edit"
            icon="pi pi-pencil"
            className="p-button-outlined"
            onClick={() => {
              const editUrl = hospitalId
                ? `/hospitals/view/${hospitalId}/contracts/${actualContractId}/edit`
                : `/payer-rules/contracts/${actualContractId}/edit`;
              navigate(editUrl);
            }}
          />
          <Button
            label="Preview"
            icon="pi pi-eye"
            className="p-button-outlined"
            onClick={() => {
              const previewUrl = hospitalId
                ? `/hospitals/view/${hospitalId}/contracts/${actualContractId}/preview`
                : `/payer-rules/contracts/${actualContractId}/preview`;
              navigate(previewUrl);
            }}
          />
          <Button
            label="Export"
            icon="pi pi-download"
            className="p-button-outlined"
            onClick={async () => {
              try {
                setIsLoading(true);
                const result = await exportFeeSchedule({
                  contractId: actualContractId!,
                  params: {},
                }).unwrap();

                // Create a download link for the CSV
                const url = window.URL.createObjectURL(result);
                const a = document.createElement("a");
                a.href = url;
                a.download = `fee-schedule-${
                  contract.provider?.name || "provider"
                }-${contract.payer?.name || "payer"}-${
                  new Date().toISOString().split("T")[0]
                }.csv`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                toastElement?.show({
                  severity: "success",
                  summary: "Export Complete",
                  detail: "Fee schedule CSV exported successfully.",
                });
              } catch (error) {
                toastElement?.show({
                  severity: "error",
                  summary: "Export Failed",
                  detail: "Failed to export fee schedule.",
                });
              } finally {
                setIsLoading(false);
              }
            }}
          />
          {contract.status === "Draft" && (
            <Button
              label="Activate Contract"
              icon="pi pi-check-circle"
              className="p-button-success"
              onClick={() => setShowGenerateDialog(true)}
            />
          )}
          {contract.status === "Active" && (
            <Button
              label="Re-generate Procedures"
              icon="pi pi-sync"
              className="p-button-outlined"
              onClick={() => setShowGenerateDialog(true)}
            />
          )}
        </div>
      </div>

      {/* Basic Information */}
      <div className="grid mb-4">
        <div className="col-12 md:col-6">
          <Card title="Contract Details" className="h-full">
            <div className="grid">
              <div className="col-6">
                <label className="block text-600 font-medium mb-1">
                  Provider
                </label>
                <div className="text-900">
                  {contract.provider?.name || "N/A"}
                </div>
              </div>
              <div className="col-6">
                <label className="block text-600 font-medium mb-1">Payer</label>
                <div className="text-900">{contract.payer?.name || "N/A"}</div>
              </div>
              <div className="col-6">
                <label className="block text-600 font-medium mb-1">
                  Effective Date
                </label>
                <div className="text-900">
                  {new Date(contract.effectiveDate).toLocaleDateString()}
                </div>
              </div>
              <div className="col-6">
                <label className="block text-600 font-medium mb-1">
                  Next Update Date
                </label>
                <div className="text-900">
                  {contract.nextUpdateDate
                    ? new Date(contract.nextUpdateDate).toLocaleDateString()
                    : "Not set"}
                </div>
              </div>
            </div>
          </Card>
        </div>
        <div className="col-12 md:col-6">
          <Card className="h-full">
            <div className="grid">
              <div className="col-6">
                <label className="block text-600 font-medium mb-1">
                  Created
                </label>
                <div className="text-900 text-sm">
                  {new Date(contract.createdAt).toLocaleString()}
                </div>
              </div>
              <div className="col-6">
                <label className="block text-600 font-medium mb-1">
                  Last Updated
                </label>
                <div className="text-900 text-sm">
                  {new Date(contract.updatedAt).toLocaleString()}
                </div>
              </div>
              <div className="col-12">
                <label className="block text-600 font-medium mb-1">
                  Created By
                </label>
                <div className="text-900">
                  {contract.createdBy?.email || "System"}
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Notes */}
      {contract.notes && (
        <Card title="Notes" className="mb-4">
          <div className="text-900 white-space-pre-wrap">{contract.notes}</div>
        </Card>
      )}

      {/* Medicare Advantage Payers */}
      {contract.payer?.type === "OriginalMedicare" &&
        contract.medicareAdvantagePlans &&
        contract.medicareAdvantagePlans.length > 0 && (
          <Card title="Medicare Advantage Payers" className="mb-4">
            <div className="flex flex-wrap gap-2">
              {contract.medicareAdvantagePlans.map((link: any) => (
                <Chip
                  key={link.medicareAdvantagePlanId}
                  label={
                    link.medicareAdvantagePlan?.name ||
                    `Payer ${link.medicareAdvantagePlanId}`
                  }
                />
              ))}
            </div>
          </Card>
        )}

      {/* Rate Calculations */}
      <Card title="Rate Calculations" className="mb-4">
        {contract.rateCalculations && contract.rateCalculations.length > 0 ? (
          <DataTable
            value={contract.rateCalculations}
            responsiveLayout="scroll"
            className="p-datatable-sm"
          >
            <Column body={rateCalculationBodyTemplate} header="Rate Details" />
          </DataTable>
        ) : (
          <div className="text-center p-4 text-600">
            No rate calculations configured for this contract.
          </div>
        )}
      </Card>

      {/* Carve-Outs */}
      <Card title="Carve-Out Files" className="mb-4">
        {contract.carveOutFiles && contract.carveOutFiles.length > 0 ? (
          <DataTable
            value={contract.carveOutFiles}
            responsiveLayout="scroll"
            className="p-datatable-sm"
          >
            <Column field="fileName" header="File Name" />
            <Column
              field="createdAt"
              header="Upload Date"
              body={(rowData) =>
                new Date(rowData.createdAt).toLocaleDateString()
              }
            />
            <Column
              header="Rows"
              body={(rowData) => {
                const rowCount = rowData.metadata?.rowCount;
                return rowCount !== undefined
                  ? `${rowCount.toLocaleString()} rows`
                  : "-";
              }}
            />
            <Column
              header="Actions"
              body={(rowData) => (
                <Button
                  icon="pi pi-download"
                  className="p-button-rounded p-button-text p-button-sm"
                  tooltip="Download"
                  onClick={async () => {
                    try {
                      setIsLoading(true);
                      // Create a download link using the S3 key
                      const apiUrl =
                        (window as any).config?.apiUrl ||
                        "http://localhost:3000";
                      const response = await fetch(
                        `${apiUrl}/contract-fee-schedules/${id}/carveouts/${rowData.id}/download`,
                        {
                          headers: {
                            Authorization: `Bearer ${token}`,
                          },
                        }
                      );

                      if (!response.ok) {
                        throw new Error("Download failed");
                      }

                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = rowData.fileName;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      window.URL.revokeObjectURL(url);
                    } catch (error) {
                      toastElement?.show({
                        severity: "error",
                        summary: "Download Failed",
                        detail: "Failed to download carveout file.",
                      });
                    } finally {
                      setIsLoading(false);
                    }
                  }}
                />
              )}
            />
          </DataTable>
        ) : (
          <div className="text-center p-4 text-600">
            No carve-out files uploaded for this contract.
          </div>
        )}
      </Card>

      {/* MPPR Rules */}
      <Card
        title="Multiple Procedure Payment Reduction Rulesets"
        className="mb-4"
      >
        {contract.mpdrRulesets && contract.mpdrRulesets.length > 0 ? (
          <div>
            {contract.mpdrRulesets.filter(Boolean).map((ruleset, index) => (
              <div
                key={ruleset.id}
                className={
                  index <
                  (contract.mpdrRulesets?.filter(Boolean).length ?? 0) - 1
                    ? "pb-3 mb-3 border-bottom"
                    : ""
                }
              >
                <div className="flex justify-content-between align-items-start mb-3">
                  <div>
                    <h4 className="mt-0 mb-2">
                      <a
                        href={`/payer-rules/mppr/edit/${ruleset.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          // Only navigate if in admin context (hospital staff can't access these pages)
                          if (!hospitalId) {
                            navigate(`/payer-rules/mppr/edit/${ruleset.id}`);
                          }
                        }}
                        className="text-primary no-underline hover:underline cursor-pointer"
                      >
                        {ruleset.groupName || "Unnamed MPDR Rule Set"}
                      </a>
                    </h4>
                    <p className="text-600 mb-0">
                      {ruleset.provider?.name} - {ruleset.payer?.name}
                    </p>
                  </div>
                </div>

                <div className="grid">
                  <div className="col-12 md:col-6">
                    <div className="text-600 text-sm mb-1">
                      Discount Percentages
                    </div>
                    <div className="text-900">
                      <div>Rank 2: {ruleset.discountPercentForRank2}%</div>
                      <div>Rank 3: {ruleset.discountPercentForRank3}%</div>
                      <div>Rank 4: {ruleset.discountPercentForRank4}%</div>
                      <div>Rank 5+: {ruleset.discountPercentForRank5Plus}%</div>
                    </div>
                  </div>
                  <div className="col-12 md:col-6">
                    <div className="text-600 text-sm mb-1">CPT Codes</div>
                    <div className="text-900">
                      {ruleset.cptCodes && ruleset.cptCodes.length > 0 ? (
                        <div>
                          {ruleset.cptCodes.slice(0, 10).join(", ")}
                          {ruleset.cptCodes.length > 10 && (
                            <span className="text-600">
                              {" "}
                              and {ruleset.cptCodes.length - 10} others
                            </span>
                          )}
                        </div>
                      ) : (
                        "No codes configured"
                      )}
                    </div>
                    {ruleset.allowOnDuplicateCptCodes && (
                      <div className="mt-2">
                        <i className="pi pi-check-circle text-green-500 mr-2" />
                        <span className="text-sm">
                          Allows duplicate CPT codes
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center p-4 text-600">
            No MPPR rules configured for this contract.
          </div>
        )}
      </Card>

      {/* Case Rates */}
      <Card title="Case Rate Rulesets" className="mb-4">
        {contract.caseRateRulesets && contract.caseRateRulesets.length > 0 ? (
          <div>
            {contract.caseRateRulesets.filter(Boolean).map((ruleset, index) => (
              <div
                key={ruleset.id}
                className={
                  index <
                  (contract.caseRateRulesets?.filter(Boolean).length ?? 0) - 1
                    ? "pb-3 mb-3 border-bottom"
                    : ""
                }
              >
                <div className="flex justify-content-between align-items-start mb-3">
                  <div>
                    <h4 className="mt-0 mb-2">
                      <a
                        href={`/payer-rules/case-rate/edit/${ruleset.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          // Only navigate if in admin context (hospital staff can't access these pages)
                          if (!hospitalId) {
                            navigate(
                              `/payer-rules/case-rate/edit/${ruleset.id}`
                            );
                          }
                        }}
                        className="text-primary no-underline hover:underline cursor-pointer"
                      >
                        {ruleset.groupName || "Unnamed Case Rate Rule Set"}
                      </a>
                    </h4>
                    <p className="text-600 mb-0">
                      {ruleset.provider?.name} - {ruleset.payer?.name}
                    </p>
                  </div>
                </div>

                <div className="grid">
                  <div className="col-12 md:col-6">
                    <div className="text-600 text-sm mb-1">
                      Included CPT Codes
                    </div>
                    <div className="text-900">
                      {ruleset.cptCodes && ruleset.cptCodes.length > 0 ? (
                        <div>
                          {ruleset.cptCodes.slice(0, 10).join(", ")}
                          {ruleset.cptCodes.length > 10 && (
                            <span className="text-600">
                              {" "}
                              and {ruleset.cptCodes.length - 10} others
                            </span>
                          )}
                        </div>
                      ) : (
                        "No codes included"
                      )}
                    </div>
                  </div>
                  <div className="col-12 md:col-6">
                    <div className="text-600 text-sm mb-1">
                      Excluded CPT Codes
                    </div>
                    <div className="text-900">
                      {ruleset.excludedCptCodes &&
                      ruleset.excludedCptCodes.length > 0 ? (
                        <div>
                          {ruleset.excludedCptCodes.slice(0, 10).join(", ")}
                          {ruleset.excludedCptCodes.length > 10 && (
                            <span className="text-600">
                              {" "}
                              and {ruleset.excludedCptCodes.length - 10} others
                            </span>
                          )}
                        </div>
                      ) : (
                        "No codes excluded"
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center p-4 text-600">
            No case rate rules configured for this contract.
          </div>
        )}
      </Card>

      {/* Generate Procedures Dialog */}
      <Dialog
        header={
          contract?.status === "Draft"
            ? "Activate Contract"
            : "Generate Provider Procedures"
        }
        visible={showGenerateDialog}
        style={{ width: "600px" }}
        onHide={() => {
          setShowGenerateDialog(false);
          setDryRunResult(null);
          setAcknowledgeOverwrite(false);
        }}
        footer={
          <div>
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={() => {
                setShowGenerateDialog(false);
                setDryRunResult(null);
                setAcknowledgeOverwrite(false);
              }}
              className="p-button-text"
            />
            <Button
              label={getButtonLabel(contract?.status, dryRunResult)}
              icon="pi pi-check"
              disabled={!acknowledgeOverwrite}
              onClick={async () => {
                try {
                  setIsLoading(true);

                  // If contract is Draft, activate it first
                  if (contract?.status === "Draft") {
                    await activateContract(actualContractId!).unwrap();
                  }

                  const result = await generateProcedures({
                    contractId: actualContractId!,
                    options: { overwriteExisting: true },
                  }).unwrap();

                  // Since the endpoint returns 202 Accepted for async processing
                  if (result.status === "processing") {
                    toastElement?.show({
                      severity: "info",
                      summary: "Import Started",
                      detail:
                        "The fee schedule import has started and will take a few minutes. The page will refresh automatically when complete.",
                      life: 10000,
                    });
                  } else {
                    // Legacy handling if backend still returns sync response
                    toastElement?.show({
                      severity: "success",
                      summary:
                        contract?.status === "Draft"
                          ? "Contract Activated"
                          : "Success",
                      detail:
                        contract?.status === "Draft"
                          ? `Contract activated and ${(
                              result.message || "procedures generated"
                            ).toLowerCase()}`
                          : result.message ||
                            "Procedures generated successfully",
                    });
                  }
                  setShowGenerateDialog(false);
                  setDryRunResult(null);
                  setAcknowledgeOverwrite(false);
                } catch (error: any) {
                  toastElement?.show({
                    severity: "error",
                    summary:
                      contract?.status === "Draft"
                        ? "Activation Failed"
                        : "Generation Failed",
                    detail: error.data?.message || "Failed to process request.",
                  });
                } finally {
                  setIsLoading(false);
                }
              }}
            />
          </div>
        }
      >
        <div className="p-fluid">
          <Message
            severity={contract?.status === "Draft" ? "warn" : "info"}
            text={
              contract?.status === "Draft"
                ? `This will activate the contract and generate provider procedures from the calculated rates for ${contract.provider?.name} and ${contract.payer?.name}. Once activated, the contract will be used for order processing.`
                : `This will generate additional provider procedures from the contract's calculated rates for ${contract.provider?.name} and ${contract.payer?.name}.`
            }
            className="mb-4"
          />

          <div className="field-checkbox mb-4">
            <Checkbox
              inputId="acknowledgeOverwrite"
              checked={acknowledgeOverwrite}
              onChange={(e) => setAcknowledgeOverwrite(e.checked || false)}
            />
            <label htmlFor="acknowledgeOverwrite" className="ml-2">
              I acknowledge that this will overwrite any existing fee schedules
              for this provider and payer
            </label>
          </div>

          {dryRunResult && (
            <div className="mt-4">
              <h4>Preview Results</h4>
              <p>
                <strong>Total Procedures:</strong> {dryRunResult.procedureCount}
              </p>
              <p>
                <strong>Total Rate:</strong> $
                {dryRunResult.totalRate?.toFixed(2)}
              </p>

              {dryRunResult.procedures &&
                dryRunResult.procedures.length > 0 && (
                  <>
                    <h5 className="mt-3">Sample Procedures (First 10):</h5>
                    <DataTable
                      value={dryRunResult.procedures}
                      className="p-datatable-sm"
                      scrollable
                      scrollHeight="200px"
                    >
                      <Column field="cptCode" header="CPT Code" />
                      <Column field="description" header="Description" />
                      <Column
                        field="priceInCents"
                        header="Price"
                        body={(rowData) =>
                          `$${(rowData.priceInCents / 100).toFixed(2)}`
                        }
                      />
                    </DataTable>
                  </>
                )}
            </div>
          )}
        </div>
      </Dialog>
    </div>
  );
}

export default ContractViewPage;
